/**
 * Quick Links + UI preferences panel that slides in from the right and is
 * styled to match the floating agent-chat panel: rounded card with a dark
 * teal header, an avatar circle, title + subtitle, and icon affordances
 * (edit / close) anchored top-right.
 */
import { Pencil, Star, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { QuickLink, UiPreferences } from '@/api/uiPreferences';
import { HomepageSelector } from '@/components/preferences/HomepageSelector';
import { QuickLinksEditor } from '@/components/preferences/QuickLinksEditor';
import { resolveQuickLinks } from '@/components/sidebar/resolveQuickLinks';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { usePermissions } from '@/hooks/usePermissions';
import { useUiPreferences, useUpdateUiPreferences } from '@/hooks/useUiPreferences';
import { logger } from '@/lib/logger';
import { useFeatureFlagsContext } from '@/providers/FeatureFlagsProvider';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
}

const EMPTY_DRAFT: UiPreferences = { quick_links: [], homepage_path: null };

export function QuickLinksDrawer({ open, onOpenChange, clientId }: Props) {
  const { hasPermission } = usePermissions();
  const { data: prefs, isLoading } = useUiPreferences();
  const updateMutation = useUpdateUiPreferences();
  const { flags } = useFeatureFlagsContext();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draft, setDraft] = useState<UiPreferences>(EMPTY_DRAFT);

  useEffect(() => {
    if (!isLoading) {
      setDraft({
        quick_links: prefs.quick_links,
        homepage_path: prefs.homepage_path,
      });
    }
  }, [prefs, isLoading]);

  // Reset to view mode every time the drawer is reopened so the user
  // always lands on the list first.
  useEffect(() => {
    if (open) setMode('view');
  }, [open]);

  // Pass `flags` so saved Quick Links targeting feature-flag-gated
  // routes (Marketplace, Carts, Wearables …) are filtered out when the
  // underlying feature is disabled — matches MemberSidebar. (M2)
  const resolved = resolveQuickLinks(prefs.quick_links, hasPermission, clientId, flags);
  const close = () => onOpenChange(false);

  const isDirty =
    JSON.stringify(draft) !==
    JSON.stringify({
      quick_links: prefs.quick_links,
      homepage_path: prefs.homepage_path,
    });

  const handleSave = () => {
    updateMutation.mutate(draft, {
      onSuccess: () => {
        toast.success('Preferences saved');
        setMode('view');
      },
      onError: (err) => {
        logger.error('Failed to save UI preferences', err);
        toast.error('Could not save preferences');
      },
    });
  };

  const handleCancel = () => {
    setDraft({
      quick_links: prefs.quick_links,
      homepage_path: prefs.homepage_path,
    });
    setMode('view');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="inset-y-5 right-5 z-[60] flex h-auto w-[calc(100%-2.5rem)] flex-col gap-0 overflow-hidden rounded-3xl border-0 bg-white p-0 shadow-2xl sm:max-w-md"
      >
        {/* Chat-style header */}
        <div className="flex items-start justify-between gap-3 bg-gradient-to-r from-[#003E3A] to-[#00625B] p-4 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
              <Star className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold leading-tight">
                {mode === 'view' ? 'Quick Links' : 'Edit Quick Links'}
              </h3>
              <p className="text-xs text-white/70">
                {mode === 'view'
                  ? 'Your saved shortcuts and homepage.'
                  : 'Tweak shortcuts and your sign-in landing page.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {mode === 'view' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMode('edit')}
                className="h-8 w-8 text-white hover:bg-white/20"
                aria-label="Edit quick links"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={close}
              className="h-8 w-8 text-white hover:bg-white/20"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 pt-6 pb-10">
          {mode === 'view' ? (
            <ViewMode
              isLoading={isLoading}
              resolved={resolved}
              homepagePath={prefs.homepage_path}
              onLinkClick={close}
            />
          ) : (
            <EditMode isLoading={isLoading} draft={draft} onChange={setDraft} flags={flags} />
          )}
        </div>

        {/* Footer (edit-mode only) */}
        {mode === 'edit' && (
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-white p-4 shadow-[0_-8px_16px_-12px_rgba(0,0,0,0.15)]">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ViewMode({
  isLoading,
  resolved,
  homepagePath,
  onLinkClick,
}: {
  isLoading: boolean;
  resolved: ReturnType<typeof resolveQuickLinks>;
  homepagePath: string | null;
  onLinkClick: () => void;
}) {
  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading…</p>;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        {resolved.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            No quick links saved yet. Tap the pencil icon above to add some.
          </div>
        ) : (
          resolved.map(({ link, catalog, href }) => {
            const Icon = catalog.icon;
            return (
              <Link
                key={link.id}
                to={href}
                onClick={onLinkClick}
                className="flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-100"
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className="truncate">{link.label || catalog.label}</span>
              </Link>
            );
          })
        )}
      </div>

      <div className="rounded-2xl bg-gray-50 px-4 py-3">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Homepage</p>
        <p className="mt-1 text-sm text-gray-800">
          {homepagePath ? (
            <code className="rounded bg-white px-1.5 py-0.5 text-xs">{homepagePath}</code>
          ) : (
            <span className="text-gray-500">App default</span>
          )}
        </p>
      </div>
    </div>
  );
}

function EditMode({
  isLoading,
  draft,
  onChange,
  flags,
}: {
  isLoading: boolean;
  draft: UiPreferences;
  onChange: (next: UiPreferences) => void;
  flags: ReturnType<typeof useFeatureFlagsContext>['flags'];
}) {
  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading preferences…</p>;
  }

  return (
    <div className="space-y-6">
      <HomepageSelector
        value={draft.homepage_path}
        onChange={(homepage_path) => onChange({ ...draft, homepage_path })}
        flags={flags}
      />
      <div className="border-t border-gray-100" />
      <QuickLinksEditor
        links={draft.quick_links}
        onChange={(quick_links: QuickLink[]) => onChange({ ...draft, quick_links })}
        flags={flags}
      />
    </div>
  );
}
